import type { WorkspaceActivityRecord } from '../services/activityService'

export type ActivityHeatmapBucket = {
  dateKey: string
  count: number
  records: WorkspaceActivityRecord[]
}

const dateKeyFromParts = (date: Date, timeZone?: string): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map((part) => [part.type, part.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}`
}

const addDaysToDateKey = (dateKey: string, offsetDays: number): string => {
  const [year, month, day] = dateKey.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day + offsetDays, 12, 0, 0))
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-')
}

export const activityDateKey = (
  activity: Pick<WorkspaceActivityRecord, 'createdAt'>,
  timeZone?: string,
): string | null => activity.createdAt ? dateKeyFromParts(activity.createdAt, timeZone) : null

export const buildActivityHeatmapBuckets = (
  records: readonly WorkspaceActivityRecord[],
  input: {
    days?: number
    endDate?: Date
    timeZone?: string
  } = {},
): ActivityHeatmapBucket[] => {
  const days = Math.max(1, input.days ?? 30)
  const endKey = dateKeyFromParts(input.endDate ?? new Date(), input.timeZone)
  const recordsByDate = new Map<string, WorkspaceActivityRecord[]>()
  for (const record of records) {
    const key = activityDateKey(record, input.timeZone)
    if (!key) continue
    const bucket = recordsByDate.get(key) ?? []
    bucket.push(record)
    recordsByDate.set(key, bucket)
  }

  return Array.from({ length: days }, (_, index) => {
    const dateKey = addDaysToDateKey(endKey, index - days + 1)
    const bucketRecords = recordsByDate.get(dateKey) ?? []
    return {
      dateKey,
      count: bucketRecords.length,
      records: bucketRecords,
    }
  })
}

export const activityBucketsByDate = (
  buckets: readonly ActivityHeatmapBucket[],
): ReadonlyMap<string, WorkspaceActivityRecord[]> =>
  new Map(buckets.map((bucket) => [bucket.dateKey, bucket.records]))
